"use client";

import { useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useSimulation } from "@/components/simulation-context";
import { useI18n } from "@/lib/i18n/hook";
import { Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Save, Trash2 } from "lucide-react";
import { InvestmentForm } from "../life-plan/components/investment-form";
import { getLifePlanSimulationParams } from "@/lib/life-simulation";
import { useToast } from "@/hooks/use-toast";

export default function Sidebar() {
  const { 
    isLoading, 
    runSimulation, 
    resetSimulation, 
    importFromLifePlan,
    setInitialInvestment,
    setInvestmentPeriod,
    setReturnRate,
    setVolatility,
    setInvestmentSchedule
  } = useSimulation();
  const { toast } = useToast()

  const { t } = useI18n();

  const handleImportLifePlan = () => {
    const lifePlanData = importFromLifePlan();
    if (!lifePlanData) {
      toast({
         title: t("common.error"),
         description: t("riskSimulation.noLifePlanData"),
         variant: "destructive"
      });
      return;
    }

    try {
      const params = getLifePlanSimulationParams(lifePlanData);
      setInitialInvestment(params.initialInvestment);
      setInvestmentPeriod(params.investmentPeriod);
      setReturnRate(params.returnRate);
      setVolatility(params.volatility);
      setInvestmentSchedule(params.investmentSchedule);
      
      toast({
         title: t("common.success"),
         description: t("riskSimulation.lifePlanImported"),
      });

      // Automatically run simulation with new params
      runSimulation(params.investmentSchedule);

    } catch (e) {
      console.error(e);
      toast({
         title: t("common.error"),
         description: t("riskSimulation.importFailed"),
         variant: "destructive"
      });
    }
  };

  // モバイル用のアコーディオンコンテンツ
  const formContent = <InvestmentForm />;

  return (
    <div className="w-full md:w-80 bg-background border-r">
      <div className="p-4 space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>{t("riskSimulation.simulationSettings")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="md:hidden">
              <Accordion type="single" collapsible>
                <AccordionItem value="settings">
                  <AccordionTrigger>
                    {t("riskSimulation.simulationSettings")}
                  </AccordionTrigger>
                  <AccordionContent>{formContent}</AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
            <div className="hidden md:block">{formContent}</div>
          </CardContent>
        </Card>

        <div className="flex gap-2">
          <Button
            className="flex-1"
            onClick={() => runSimulation()}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("riskSimulation.running")}
              </>
            ) : (
              t("riskSimulation.runSimulation")
            )}
          </Button>
          <Button
            variant="secondary"
            className="flex-1"
            onClick={handleImportLifePlan}
            disabled={isLoading}
          >
            {t("riskSimulation.importLifePlan")}
          </Button>
          <Button
            variant="outline"
            onClick={resetSimulation}
            disabled={isLoading}
          >
            {t("common.reset")}
          </Button>
        </div>
      </div>
    </div>
  );
}
